import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MessageCircle } from 'lucide-react';
import { injectFonts, T } from '../utils/fonts';

const Footer = () => {
  useEffect(() => { injectFonts(); }, []);

  return (
    <>
      <style>{`
        @keyframes footerGlow  { 0%,100%{opacity:.4} 50%{opacity:.9} }
        @keyframes borderSlide { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

        .footer-link {
          font-family: ${T.fontBody};
          font-size: clamp(0.85rem,3vw,0.95rem);
          color: ${T.text};
          font-weight: 600;
          text-decoration: none;
          transition: color 0.25s ease;
          position: relative;
          display: inline-block;
        }
        .footer-link::after {
          content:''; position:absolute; bottom:-2px; left:0; right:100%;
          height:1px; background:linear-gradient(90deg, ${T.godot}, ${T.violet});
          transition:right 0.3s ease;
        }
        .footer-link:hover,.footer-link:focus { color:${T.godot}; }
        .footer-link:hover::after,.footer-link:focus::after { right:0; }
        .footer-link:focus-visible { outline:2px solid #6366f1; outline-offset:3px; border-radius:3px; }

        .whatsapp-btn {
          display:inline-flex; align-items:center; justify-content:center; gap:8px;
          margin-top:6px; padding:10px 18px;
          background:linear-gradient(135deg,#10b981,#059669);
          color:white; text-decoration:none; border-radius:10px;
          font-family: ${T.fontBody};
          font-size:clamp(0.85rem,3vw,0.95rem); font-weight:600;
          transition:all 0.3s ease; box-shadow:0 4px 12px rgba(16,185,129,0.3);
          min-height:44px;
        }
        .whatsapp-btn:hover { background:linear-gradient(135deg,#059669,#047857); transform:translateY(-2px); box-shadow:0 8px 20px rgba(16,185,129,0.4); }
        .whatsapp-btn:focus-visible { outline:2px solid #10b981; outline-offset:2px; }

        .footer-legal-link {
          font-family: ${T.fontBody};
          font-size:clamp(0.75rem,2.5vw,0.85rem);
          color:${T.text}; font-weight:600; text-decoration:none; transition:color 0.25s ease;
        }
        .footer-legal-link:hover { color:${T.godot}; }
        .footer-legal-link:focus-visible { outline:3px solid ${T.focus}; outline-offset:2px; border-radius:2px; }

        .footer-contact-link {
          color:${T.text}; font-size:0.84rem; font-weight:600;
          text-decoration:none; font-family:${T.fontBody};
          display:flex; align-items:center; gap:5px;
        }
        .footer-contact-link:hover { color:${T.godot}; }
        .footer-contact-link:focus-visible { outline:3px solid ${T.focus}; outline-offset:2px; border-radius:3px; }

        /* Mobile footer — compact single-column */
        @media (max-width: 640px) {
          .footer-desktop-only { display: none !important; }
          .footer-mobile-compact { display: flex !important; }
          .footer-content-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
        }
        @media (min-width: 641px) {
          .footer-mobile-compact { display: none !important; }
        }
      `}</style>

      <footer style={styles.footer}>
        <div style={styles.topBorder} aria-hidden="true" />
        <div style={styles.gridOverlay} aria-hidden="true" />

        {/* ── DESKTOP footer (641px+) ── */}
        <div className="footer-desktop-only">
          <div style={styles.footerContent}>
            {/* Brand */}
            <div style={styles.footerSection}>
              <div style={styles.brand}>
                <span style={{ ...styles.brandName, fontFamily: T.fontDisplay }}>GoGameDev3D</span>
                <div style={styles.brandDot} aria-hidden="true" />
              </div>
              <p style={{ ...styles.footerText, fontFamily: T.fontBody }}>
                למידה מקצועית של פיתוח משחקים ותכנות לילדים ובני נוער בגילאי 10–18
              </p>
              <div style={styles.statRow}>
                {[['🎮','פיתוח משחקים'],['🐍','תכנות פייתון'],['🌐','אתרים 3D']].map(([icon,label])=>(
                  <div key={label} style={styles.statChip}>
                    <span>{icon}</span>
                    <span style={{ fontSize:'0.75rem', color:T.text, fontWeight:700, fontFamily: T.fontBody }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick links */}
            <div style={styles.footerSection}>
              <h2 style={{ ...styles.footerSubtitle, fontFamily: T.fontDisplay }}>
                <span style={styles.subtitleDot} aria-hidden="true" />קישורים מהירים
              </h2>
              <div style={styles.linksContainer}>
                {[['/', 'דף הבית'],['/courses','הקורסים'],['/contact','צור קשר'],['/about','אודות']].map(([to,label])=>(
                  <Link key={to} to={to} className="footer-link">{label}</Link>
                ))}
              </div>
            </div>

            {/* Legal */}
            <div style={styles.footerSection}>
              <h2 style={{ ...styles.footerSubtitle, fontFamily: T.fontDisplay }}>
                <span style={styles.subtitleDot} aria-hidden="true" />מידע משפטי
              </h2>
              <div style={styles.linksContainer}>
                {[['/accessibility','הצהרת נגישות'],['/terms','תקנון'],['/privacy','פרטיות']].map(([to,label])=>(
                  <Link key={to} to={to} className="footer-link">{label}</Link>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div style={styles.footerSection}>
              <h2 style={{ ...styles.footerSubtitle, fontFamily: T.fontDisplay }}>
                <span style={styles.subtitleDot} aria-hidden="true" />צור קשר
              </h2>
              <div style={styles.contactInfo}>
                <a href="tel:054-944-7314" style={styles.contactRow} className="footer-link">
                  <span style={styles.contactIcon}><Phone size={15}/></span>054-944-7314
                </a>
                <a href="mailto:gogamedev3d@gmail.com" style={styles.contactRow} className="footer-link">
                  <span style={styles.contactIcon}><Mail size={15}/></span>gogamedev3d@gmail.com
                </a>
                <a href="https://wa.me/972549447314" target="_blank" rel="noopener noreferrer" className="whatsapp-btn">
                  <MessageCircle size={17}/>WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ── MOBILE footer (≤640px) — compact ── */}
        <div className="footer-mobile-compact" style={{ flexDirection:'column', gap:16, maxWidth:500, margin:'0 auto', padding:'0 4px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
            <span style={{ ...styles.brandName, fontFamily: T.fontDisplay, fontSize:'1.1rem' }}>GoGameDev3D</span>
            <a href="https://wa.me/972549447314" target="_blank" rel="noopener noreferrer"
              style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 14px', background:'linear-gradient(135deg,#10b981,#059669)', color:'white', textDecoration:'none', borderRadius:9, fontWeight:600, fontSize:'0.82rem', fontFamily: T.fontBody, minHeight:40 }}>
              <MessageCircle size={15}/>WhatsApp
            </a>
          </div>

          <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'center' }}>
            {[['/', 'דף הבית'],['/courses','קורסים'],['/contact','צור קשר'],['/about','אודות'],['/accessibility','נגישות'],['/terms','תנאים'],['/privacy','פרטיות']].map(([to,label])=>(
              <Link key={to} to={to} className="footer-legal-link" style={{ padding:'4px 2px' }}>{label}</Link>
            ))}
          </div>

          <div style={{ display:'flex', justifyContent:'center', gap:16, flexWrap:'wrap' }}>
            <a href="tel:054-944-7314" className="footer-contact-link">
              <Phone size={13}/>054-944-7314
            </a>
            <a href="mailto:gogamedev3d@gmail.com" className="footer-contact-link">
              <Mail size={13}/>gogamedev3d@gmail.com
            </a>
          </div>
        </div>

        <div style={styles.footerBottom}>
          <div style={styles.bottomDivider} />
          <div style={{ ...styles.bottomRow, justifyContent:'center' }}>
            <p style={{ ...styles.copyrightText, fontFamily: T.fontBody, textAlign:'center' }}>
              &copy; {new Date().getFullYear()} GoGameDev3D — כל הזכויות שמורות
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

const styles = {
  footer: { backgroundColor:T.panel, color:T.muted, padding:'clamp(20px,5vw,64px) clamp(16px,5vw,48px) clamp(12px,3vw,28px)', marginTop:'auto', direction:'rtl', position:'relative', overflow:'hidden', fontFamily: T.fontBody },
  topBorder: { position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg,transparent 0%,rgba(99,102,241,0.8) 25%,rgba(6,182,212,0.8) 50%,rgba(139,92,246,0.8) 75%,transparent 100%)', backgroundSize:'200% 100%', animation:'borderSlide 5s linear infinite' },
  gridOverlay: { position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(99,102,241,0.03) 1px, transparent 1px),linear-gradient(90deg, rgba(99,102,241,0.03) 1px, transparent 1px)', backgroundSize:'60px 60px', pointerEvents:'none', zIndex:0 },
  footerContent: { maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(min(100%,180px),1fr))', gap:'clamp(24px,5vw,48px)', marginBottom:'clamp(28px,5vw,48px)', position:'relative', zIndex:1 },
  footerSection: { display:'flex', flexDirection:'column', gap:'clamp(8px,2vw,14px)' },
  brand: { display:'flex', alignItems:'center', gap:8, marginBottom:4 },
  brandName: { fontSize:'clamp(1rem,4vw,1.4rem)', fontWeight:700, letterSpacing:'-0.02em', background:`linear-gradient(90deg, ${T.text} 20%, ${T.godot} 55%, ${T.violet} 85%)`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' },
  brandDot: { width:8, height:8, borderRadius:'50%', background:'linear-gradient(135deg,#6366f1,#06b6d4)', boxShadow:'0 0 8px rgba(99,102,241,0.7)', animation:'footerGlow 2.5s ease-in-out infinite' },
  footerText: { fontSize:'clamp(0.82rem,3vw,0.9rem)', lineHeight:1.65, color:T.muted, margin:0 },
  statRow: { display:'flex', flexWrap:'wrap', gap:6, marginTop:4 },
  statChip: { display:'flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:8, background:'rgba(99,102,241,0.08)', border:'1px solid rgba(99,102,241,0.15)', fontSize:'0.75rem' },
  footerSubtitle: { fontSize:'clamp(0.9rem,3.5vw,1rem)', fontWeight:800, color:T.text, margin:'0 0 4px', display:'flex', alignItems:'center', gap:8 },
  subtitleDot: { display:'inline-block', width:6, height:6, borderRadius:'50%', background:'linear-gradient(135deg,#6366f1,#06b6d4)', flexShrink:0 },
  linksContainer: { display:'flex', flexDirection:'column', gap:'clamp(8px,2vw,12px)' },
  contactInfo: { display:'flex', flexDirection:'column', gap:'clamp(8px,2vw,10px)' },
  contactRow: { display:'flex', alignItems:'center', gap:8 },
  contactIcon: { display:'flex', alignItems:'center', color:'#6366f1', flexShrink:0 },
  footerBottom: { position:'relative', zIndex:1 },
  bottomDivider: { height:1, background:'linear-gradient(90deg,transparent,rgba(99,102,241,0.25),rgba(6,182,212,0.25),transparent)', marginBottom:'clamp(14px,3vw,24px)' },
  bottomRow: { display:'flex', flexWrap:'wrap', justifyContent:'space-between', alignItems:'center', gap:12, maxWidth:1200, margin:'0 auto' },
  copyrightText: { fontSize:'clamp(0.75rem,2.5vw,0.85rem)', color:T.muted, margin:0 },
  legalLinks: { display:'flex', alignItems:'center', gap:10 },
};

export default Footer;
